import { DateRangeValue } from "..";
import { IRole, UserLight } from "../user";
import { IGetDealsData } from "./deal";

export interface DealStage {
  id: number;
  title: string;
  status_id: number;
  order: number;
  deals_count: number;
  deals: DealStageItem[];
  is_system: boolean;
  /** Per-stage last page for flat pagination; always 1 while grouping is active. */
  deals_last_page?: number;
  /**
   * Set by the FE when `deals_group_by` was requested: the stage's group nodes (moved out of `deals`,
   * which is then emptied). Grouping disables pagination, so this is the whole stage.
   */
  groups?: DealStageGroup[];
  /**
   * Diff view (`show_stage_diff: true`) only: distinct deals that ever entered / left this stage in the
   * selected range. Whole-stage aggregates, unaffected by pagination. Absent when diff view is off.
   */
  deals_in?: number;
  deals_out?: number;
}

/**
 * One entry of a deal's stage-change timeline (`stage_diff`), returned only in diff view. For a multi-element
 * timeline the FIRST element is a backend-provided header (`date: null`, e.g. "New > Invested") shown as the
 * collapsed-band label; the rest follow oldest→newest — optional "Left and returned" (`date: null`), optional
 * "Added to {stage}" (dated), then "{OLD} > {NEW}" moves (dated). The FE renders titles as-is.
 */
export interface StageDiffEntry {
  date: string | null;
  title: string;
}

/** Date-range filter for diff view (plain `YYYY-MM-DD` days). Filters by each deal's own `created_at`.
 * `from` may be null (open lower bound). */
export interface DealsDateRange {
  from: string | null;
  to: string;
}

/**
 * A group node as returned by the backend inside a stage's `deals` (pipeline) / row list (deals-list).
 * `children` is polymorphic: subgroup nodes for the non-deepest level, deal leaves at the deepest level.
 * Decide by the grouping depth you requested, not by inspecting items.
 */
export interface DealStageGroup {
  /** Ready-to-display title, e.g. "Real Estate" or "No Vintage". */
  group_title: string;
  /** Distinct deals in this subtree — NOT the sum of children (a deal can sit in several groups). */
  deals_count: number;
  children: Array<DealStageGroup | DealStageItem>;
}

export interface DealStageItem {
  id: number;
  title: string;
  asset_class: string;
  status_id: number;
  shared_with: UserLight[];
  deal_note: DealStageItemNote | null;
  is_shared_deal?: boolean;
  role?: IRole | null;
  /** Diff view (`show_stage_diff: true`) only: the deal's stage-change timeline. For a multi-move deal the
   * FIRST element is a header `{ date: null, title }`; the rest are dated moves (oldest→newest). */
  stage_diff?: StageDiffEntry[];
  // `card_characteristics` come back as extra top-level keys spelled exactly as requested,
  // e.g. deal["Vintage"] = ["2018","2019"] | deal["Contact"] = "a@b.com" | null. Read via getCardChips.
}

export interface DealStageItemNote {
  id: number;
  note: string;
}

/** Type guard: distinguishes a group node from a deal leaf inside `children` / `deals`. */
export const isDealStageGroup = (node: DealStageGroup | DealStageItem): node is DealStageGroup =>
  (node as DealStageGroup).group_title !== undefined;

export type GetDealStagesData<T extends boolean> = Partial<Omit<IGetDealsData, "page">> & {
  with_deals: T;
  page?: number;
  freemium_dashboard?: boolean;
  /** Lowercase dimension keys, max 2, e.g. ["asset_class"] or ["asset_class","currency"]. */
  deals_group_by?: string[];
  /** Characteristic names to attach to each card, e.g. ["Asset Class", "Vintage", "Contact"]. */
  card_characteristics?: string[];
  /** Diff view: turns on per-deal `stage_diff` and per-stage `deals_in`/`deals_out`. Needs `with_deals`. */
  show_stage_diff?: boolean;
  /** Diff view: optional filter by each deal's `created_at`. Only honored when `show_stage_diff` is true. */
  dateRange?: DealsDateRange;
};

/** A Group By dimension: `key` is sent to the API, `label` is shown to the user. */
export interface PipelineDimensionOption {
  key: string;
  label: string;
}

/**
 * Pipeline view config persisted server-side via `/user/settings` (key "pipelineViewConfig") — the endpoint
 * stores an arbitrary JSON blob. Shape mirrors the relevant `DealsQueries` fields for direct hydration.
 */
export interface PipelineViewConfig {
  pipelineGroupBy?: string[];
  pipelineCardCharacteristics?: string[];
  pipelineCardCharacteristicOptions?: string[];
  pipelineHiddenStageIds?: number[];
  /** Diff View toggle. */
  pipelineDiffView?: boolean;
  /** Diff View date range stored as the reports-native value (preset key + dates); relative presets are
   * re-resolved with `convertDateRange` at request time so they stay current. */
  pipelineDateRange?: DateRangeValue | null;
}

/**
 * Deal-list (table) view config persisted server-side via `/user/settings` (key "dealListViewConfig") — the
 * endpoint stores an arbitrary JSON blob. Shape mirrors the relevant `DealsQueries` fields for direct hydration.
 */
export interface DealListViewConfig {
  dealListGroupBy?: string[];
  dealListCharacteristics?: string[];
  dealListCharacteristicsOptions?: string[];
}

export interface CreateDealStageData {
  title: string;
  status_id: number;
}

export interface UpdateDealStageData extends Partial<CreateDealStageData> {
  id: number;
}

export type ReorderDealStagesData = {
  order: Array<{ id: number; position: number }>;
};
